/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import { generateClient } from 'aws-amplify/api';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import type { Subscription } from 'rxjs';
import { fetchNotifications, StyledBadge } from '@/components/pages/menu';
import Calendar from '@/components/shared/image/icon_calendar.svg';
import Car from '@/components/shared/image/icon_car.svg';
import Menu from '@/components/shared/image/icon_menu.svg';
import * as subscriptions from '@/graphql/subscriptions';
import { useServiceType } from '@/modules/hooks/useServiceType';
import * as paths from '@/routes/paths';
import { settingState } from '@/state/settingState';
import { ServiceType } from '@/types/serviceType';

const footerMenuStyle = css`
  width: 100%;
  position: fixed;
  top: auto;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1200;
  box-shadow: 0px -2px 1px -1px rgba(0, 0, 0, 0.2);

  /* フッターアイコン: 非選択時はグレー */
  img.footer-icon {
    width: 1.5rem;
    height: 1.5rem;
    object-fit: contain;
    filter: brightness(0) saturate(100%) invert(60%);
    transition: filter 0.2s ease;
  }

  /* 選択中のアイコンを青色（#1976d2）に */
  .Mui-selected img.footer-icon {
    filter: brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(186deg) brightness(92%)
      contrast(97%) !important;
  }
`;
const Footer = () => {
  const [currentTab, setCurrentTab] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [setting] = useRecoilState(settingState);
  const [isSubApiSuccess, setIsSubApiSuccess] = useState(false);
  const [isListApiSuccess, setIsListApiSuccess] = useState(false);
  const [onlineOfflineStatus, setOnlineOfflineStatus] = useState(-1); // -1：デフォルト値（初期化とする）、０：オフライン、１：再オンライン
  const serviceType = useServiceType();

  // ページのURLに応じて現在のタブの状態を設定する
  useEffect(() => {
    const path = location.pathname;
    if (path.includes(paths.driveList)) {
      setCurrentTab('driveList');
    } else if (path.includes(paths.offering)) {
      setCurrentTab('offering');
    } else if (path.includes(paths.menu)) {
      setCurrentTab('menu');
    }
  }, [location.pathname]);

  const [unreadCount, setUnreadCount] = useState(0);
  // ドライバID（cognitoのdriverId）
  const driverId = setting?.driverId || '';

  // フッタ移動時のドライバ個別通知購読API
  const tabClickCreateSubscribe = () => {
    const client = generateClient();
    const variables = {
      createdAt: { lt: new Date().toDateString() },
      driverId,
      filter: {
        isRead: { eq: false },
        driverId: { eq: driverId },
      },
    };
    const createSub = client
      .graphql({ query: subscriptions.onCreateDriverIndividualNotification, variables })
      .subscribe({
        next: () => {
          setUnreadCount((prevCount) => prevCount + 1);
        },
        error: () => {
          // ドライバ個別通知購読API失敗
          setIsSubApiSuccess(false);
          createSub.unsubscribe();
        },
      });

    if (createSub && !createSub.closed) {
      // ドライバ個別通知購読API成功
      setIsSubApiSuccess(true);
    }
  };

  // フッタ移動時のドライバ個別通知一覧取得API
  const tabClickGetUnreadCount = async () => {
    const [responseDriverIndividualNotifications, isCallApiSuccess] = await fetchNotifications(driverId);
    // ドライバ個別通知一覧取得API成功フラグを設定する
    setIsListApiSuccess(isCallApiSuccess);

    if (!responseDriverIndividualNotifications || responseDriverIndividualNotifications.length === 0) {
      setUnreadCount(0);

      return;
    }

    const unreadNotifications = responseDriverIndividualNotifications.filter((item) => !item?.isRead);
    setUnreadCount(unreadNotifications.length);
  };

  // フッタ移動時
  const tabClick = () => {
    if (!isSubApiSuccess) {
      // フッタ移動時のドライバ個別通知購読API
      tabClickCreateSubscribe();

      // フッタ移動時のドライバ個別通知一覧取得API
      void tabClickGetUnreadCount();
    } else if (!isListApiSuccess) {
      // フッタ移動時のドライバ個別通知一覧取得API
      void tabClickGetUnreadCount();
    }
  };

  const handleChange = (_: React.SyntheticEvent, newTab: string) => {
    setCurrentTab(newTab); // タブの状態を更新

    tabClick(); // フッタ移動時

    switch (newTab) {
      case 'driveList':
        navigate(paths.driveList);
        break;
      case 'offering':
        navigate(paths.offering);
        break;
      case 'menu':
        navigate(paths.menu);
        break;
      default:
        navigate(paths.top);
        break;
    }
  };

  useEffect(() => {
    const handleOnline = () => {
      // １：再オンライン
      setOnlineOfflineStatus(1);
    };

    const handleOffline = () => {
      // ０：オフライン
      setOnlineOfflineStatus(0);
      // ドライバ個別通知購読API失敗
      setIsSubApiSuccess(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    // 画面初期処理
    const getUnreadCount = async () => {
      const [responseDriverIndividualNotifications, isCallApiSuccess] = await fetchNotifications(driverId);
      // ドライバ個別通知一覧取得API成功フラグを設定する
      setIsListApiSuccess(isCallApiSuccess);

      if (!responseDriverIndividualNotifications || responseDriverIndividualNotifications.length === 0) {
        setUnreadCount(0);

        return;
      }

      const unreadNotifications = responseDriverIndividualNotifications.filter((item) => !item?.isRead);
      setUnreadCount(unreadNotifications.length);
    };

    // オフライン以外の場合（初期処理と再オンライン）、
    if (onlineOfflineStatus !== 0) {
      // ドライバ個別通知購読APIを実行後にドライバ個別通知一覧取得APIを実行する。
      if (isSubApiSuccess) {
        void getUnreadCount();
      }
    }
  }, [driverId, isSubApiSuccess, onlineOfflineStatus]);

  // リアルタイム
  useEffect(() => {
    const subscription: Subscription[] = [];
    const subscribeWithRetry = (retryCount = 3) => {
      if (retryCount >= 0) {
        const client = generateClient();
        const variables = {
          createdAt: { lt: new Date().toDateString() },
          driverId,
          filter: {
            isRead: { eq: false },
            driverId: { eq: driverId },
          },
        };
        const createSub = client
          .graphql({ query: subscriptions.onCreateDriverIndividualNotification, variables })
          .subscribe({
            next: () => {
              setUnreadCount((prevCount) => prevCount + 1);
            },
            error: () => {
              // ドライバ個別通知購読API失敗
              setIsSubApiSuccess(false);
              createSub.unsubscribe();
              setTimeout(() => subscribeWithRetry(retryCount - 1), 2000);
            },
          });

        if (createSub && !createSub.closed) {
          // ドライバ個別通知購読API成功
          setIsSubApiSuccess(true);
        }

        subscription.push(createSub);
      }
    };

    // オフライン以外の場合（初期処理と再オンライン）、
    if (onlineOfflineStatus !== 0) {
      // リトライ回数（３回）
      subscribeWithRetry(3);
    }

    return () => {
      if (subscription && subscription.length >= 0) {
        subscription.forEach((element) => {
          !element.closed && element.unsubscribe();
        });
      }
    };
  }, [driverId, onlineOfflineStatus]);

  // リアルタイム
  useEffect(() => {
    const subscription: Subscription[] = [];
    const subscribeWithRetry = (retryCount = 3) => {
      if (retryCount >= 0) {
        const client = generateClient();
        const variables = {
          createdAt: { lt: new Date().toDateString() },
          driverId,
          filter: {
            driverId: { eq: driverId },
            isRead: { eq: true },
          },
        };
        const createSub = client
          .graphql({ query: subscriptions.onUpdateDriverIndividualNotification, variables })
          .subscribe({
            next: () => {
              setUnreadCount((prevCount) => prevCount - 1);
            },
            error: () => {
              createSub.unsubscribe();
              setTimeout(() => subscribeWithRetry(retryCount - 1), 2000);
            },
          });

        subscription.push(createSub);
      }
    };

    // オフライン以外の場合（初期処理と再オンライン）、
    if (onlineOfflineStatus !== 0) {
      // リトライ回数（３回）
      subscribeWithRetry(3);
    }

    return () => {
      if (subscription && subscription.length >= 0) {
        subscription.forEach((element) => {
          !element.closed && element.unsubscribe();
        });
      }
    };
  }, [driverId, onlineOfflineStatus]);

  const footerFontSize = 'min(4vw, 0.875rem)';

  return (
    <BottomNavigation
      value={currentTab}
      onChange={handleChange}
      showLabels
      css={footerMenuStyle}
      sx={{
        '& .Mui-selected': {
          color: '#1976d2 !important',
        },
      }}
    >
      <BottomNavigationAction
        label={<span style={{ fontSize: footerFontSize }}>配車予約</span>}
        value="driveList"
        icon={<img src={Calendar} alt="" className="footer-icon" />}
      />
      {serviceType === ServiceType.PreBooking && (
        <BottomNavigationAction
          label={<span style={{ fontSize: footerFontSize }}>お仕事を探す</span>}
          value="offering"
          icon={<img src={Car} alt="" className="footer-icon" />}
        />
      )}
      <BottomNavigationAction
        label={<span style={{ fontSize: footerFontSize }}>メニュー</span>}
        value="menu"
        icon={
          <StyledBadge badgeContent={unreadCount}>
            <img src={Menu} alt="" className="footer-icon" />
          </StyledBadge>
        }
      />
    </BottomNavigation>
  );
};

export default Footer;
